import './style.css'

import { InlineWarning } from '@driftt/tide-core';
import { Select } from '@driftt/tide-selects'
import { Table } from '@driftt/tide-table'
import { updateEventSubscriptions } from 'api/developerApi'
import ConditionalRender from 'components/ConditionalRender'
import moment from 'moment'
import PropTypes from 'prop-types'
import { contains, filter, isEmpty, reject, without } from 'ramda'
import React, { Component } from 'react'

import trashBinSrc from './trashBin.svg'

const ACTIONS_LABEL = ''
const SUBSCRIPTION_TABLE_COLUMNS = [
  { label: 'Event Name' },
  { label: 'Description' },
  { label: 'Required Scope' },
  { label: ACTIONS_LABEL }
]

const CONVO_STATUS_EVENT = 'conversation_status_updated'
const CONVO_STATUS_UPDATE_DEP_TIME = moment('2020/04/15')

const DeleteButton = ({ onClick }) => (
  <img
    className="app-events-delete-icon"
    src={trashBinSrc}
    onClick={onClick}
    alt="delete"
  />
)

class AppEventsSection extends Component {

  static propTypes = {
    isSelectorDisabled: PropTypes.bool.isRequired,
    subscriptions: PropTypes.array.isRequired,
    appId: PropTypes.string.isRequired,
    showErrorNotification: PropTypes.func.isRequired,
    updateSubscriptions: PropTypes.func.isRequired,
    eventDescriptions: PropTypes.array.isRequired
  }

  constructor(props) {
    super(props)
    this.state = {
      subscriptions: props.subscriptions
    }
  }

  resolveAvailableEventTypes = () => {
    const { subscriptions } = this.state
    const { eventDescriptions } = this.props
    const isSubscribed = (eventDescription) => contains(eventDescription.event, subscriptions)
    return reject(isSubscribed, eventDescriptions)
  }

  componentWillReceiveProps(props) {
    this.setState({ subscriptions: props.subscriptions })
  }

  resolveUsedEventTypes = () => {
    const { subscriptions } = this.state
    const { eventDescriptions } = this.props
    const isSubscribed = (eventDescription) => contains(eventDescription.event, subscriptions)
    return filter(isSubscribed, eventDescriptions)
  }

  resolveAvailableSubscriptionOptions = () => {
    return this.resolveAvailableEventTypes()
      .map(eventDescription => ({
        value: eventDescription.event,
        label: eventDescription.event,
        disabled: moment().isAfter(CONVO_STATUS_UPDATE_DEP_TIME) && eventDescription.event === CONVO_STATUS_EVENT
      }))
  }

  addSubscription = ({ value }) => {
    const { appId, updateSubscriptions, showErrorNotification } = this.props
    const { subscriptions } = this.state
    const updatedSubscriptions = [...subscriptions, value]

    const onError = (error) => {
      showErrorNotification('Ooops, something went wrong. Please try again.')
      this.setState({ subscriptions })
    }

    const onStateSet = () => updateEventSubscriptions({ subscriptions: updatedSubscriptions, appId })
      .then(() => updateSubscriptions(updatedSubscriptions))
      .catch(onError)

    this.setState({ subscriptions: updatedSubscriptions }, onStateSet)
  }

  removeSubscription = (value) => {
    const { appId, updateSubscriptions, showErrorNotification } = this.props
    const { subscriptions } = this.state
    const updatedSubscriptions = without([value], subscriptions)

    const onError = (error) => {
      showErrorNotification('Ooops, something went wrong. Please try again.')
      this.setState({ subscriptions })
    }

    const onStateSet = () => updateEventSubscriptions({ subscriptions: updatedSubscriptions, appId })
      .then(() => updateSubscriptions(updatedSubscriptions))
      .catch(onError)

    this.setState({ subscriptions: updatedSubscriptions }, onStateSet)
  }

  resolveSubscriptionTableRows = () => {
    return this.resolveUsedEventTypes().map(eventDescription => [
      { content: eventDescription.event },
      { content: eventDescription.description },
      { content: eventDescription.requiredScopes[0] },
      {
        content: <DeleteButton onClick={() =>
          this.removeSubscription(eventDescription.event)} />
      },
    ])
  }

  render() {
    const { isSelectorDisabled } = this.props
    const availableSubscriptionOptions = this.resolveAvailableSubscriptionOptions()
    const subscriptionTableRows = this.resolveSubscriptionTableRows()
    const subscribedToStatusChange = subscriptionTableRows.some(row => row[0] && row[0].content === CONVO_STATUS_EVENT)

    return (
      <div className="app-events-section">
        <span className="app-events-section-title">
          Subscribe to Webhook Events
        </span>
        <p className="app-events-section-description">
          To subscribe to a particular webhook event, your app must have access to the related OAuth permission scope.
        </p>

        <ConditionalRender
          condition={!isEmpty(availableSubscriptionOptions)}
          className="app-events-section-select">
          <Select
            label="Add Event"
            placeholder="Select an event"
            size="medium"
            searchable={false}
            options={availableSubscriptionOptions}
            disabled={isSelectorDisabled}
            onChange={this.addSubscription}
            className="app-events-section-select-component"
          />
        </ConditionalRender>

        <ConditionalRender
          condition={!isEmpty(subscriptionTableRows)}
          className="app-events-section-table">

          {subscribedToStatusChange && <InlineWarning
            className="app-events-status-warning"
            title={<span>'{CONVO_STATUS_EVENT}' is deprecated!</span>}
            type="warning"
            copy={<span>Conversation status is being phased out of Drift and the '{CONVO_STATUS_EVENT}' event is deprecated. Please see <a target="_blank" rel="noopener noreferrer" href="https://devdocs.drift.com/docs/webhooks-deprecating-conversation-status">our docs</a> for more information</span>}
          />}
          <Table
            columns={SUBSCRIPTION_TABLE_COLUMNS}
            data={subscriptionTableRows.map(row => {
              return { data: row }
            })}
          />
        </ConditionalRender>
      </div>
    )
  }
}

export default AppEventsSection
